import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeftRight, Lock, Play, Star, Target, Users } from "lucide-react";
import Sparkline, { DeltaChip } from "@/components/Sparkline";
import { milestoneLabel } from "@/milestones";

export const MILESTONE_ICONS = { squad: Users, debut: Play, start: Star, goal_assist: Target, intl: Star, transfer: ArrowLeftRight };

// Nature of the club change, from the provider's own transfer `type` — a loan is named as a
// loan instead of a bare "club change", and nothing is inferred when the type is unknown.
const TRANSFER_KIND = { loan: "transfer_loan", "return from loan": "transfer_loan_return", transfer: "transfer_permanent", free: "transfer_permanent" };

export function fmtDate(d, lang) {
  if (!d) return "—";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { year: "numeric", month: "short", day: "numeric" });
}

export function SignalPanel({ score, reasons, signal, series, delta7d, locked, noScoreReason, t, lang }) {
  return (
    <div className={`rounded-xl border p-5 hud-card ${score >= 80 ? "border-[#00FF87]/80 shadow-[0_0_26px_rgba(0,255,135,0.25)]" : score >= 60 ? "border-[#FFD600]/70 shadow-[0_0_22px_rgba(255,214,0,0.18)]" : ""}`}>
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-slate-100">{t("signal.title")}</h2>
        <span className={`font-mono2 text-4xl font-bold ${score >= 80 ? "neon-text" : score >= 60 ? "text-[#FFD600]" : score > 0 ? "text-[#00F0FF]" : "text-slate-400"}`} data-testid="player-signal-score-badge">
          {score === null || score === undefined ? "—" : score}
        </span>
      </div>
      {locked && (
        <Link to="/pricing" data-testid="signal-trend-locked" className="mt-3 flex items-center gap-2 rounded-lg border border-dashed border-[#FFD600]/40 px-3 py-2.5 font-mono2 text-[10px] uppercase tracking-wider text-[#FFD600]/80 hover:bg-[#FFD600]/5">
          <Lock className="h-3.5 w-3.5" /> {t("trends.trend_30d")} · {t("trends.locked")}
        </Link>
      )}
      {series?.length > 0 && (
        <div className="mt-3 flex items-center gap-3" data-testid="signal-trend">
          <Sparkline points={series} width={150} height={36} testid="signal-trend-sparkline" title={t("trends.trend_30d")} />
          <div className="flex flex-col gap-1">
            <DeltaChip delta={delta7d} testid="signal-delta-7d" />
            <span className="font-mono2 text-[9px] uppercase tracking-wider text-slate-500">{t("trends.trend_30d")}</span>
          </div>
        </div>
      )}
      <ul className="mt-4 space-y-2.5" data-testid="signal-breakdown">
        {score === null && (
          // A 0 reads "no attention"; a dash reads "no data". With no derived milestone there
          // is nothing to total, so the breakdown is replaced instead of showing TOTAL 0.
          <li className="font-mono2 text-[11px] uppercase leading-relaxed tracking-wider text-slate-500" data-testid="signal-no-data">
            {noScoreReason?.code === "untracked_competition"
              ? t("signal.no_data_untracked").replace("{comps}", (noScoreReason.competitions || []).join(", "))
              : noScoreReason?.code === "fixture_not_processed"
              ? t("signal.no_data_pending")
              : t("signal.no_data")}
          </li>
        )}
        {reasons.map((r, i) => (
          <li key={`${r.key}-${r.points}`} className="flex items-start justify-between gap-2 text-sm text-slate-300" data-testid={`signal-reason-${i}`}>
            <span className="flex items-start gap-2">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#00FF87] shadow-[0_0_6px_rgba(0,255,135,0.8)]" />
              <span>{t(`reasons.${r.key}`, r.params || {})}</span>
            </span>
            <span className="shrink-0 font-mono2 text-xs font-bold text-[#00FF87] tabular-nums" data-testid={`signal-reason-points-${i}`}>+{r.points}</span>
          </li>
        ))}
        {reasons.length > 0 && (
          <li className="flex items-center justify-between border-t border-[#1F293D] pt-2 font-mono2 text-xs uppercase tracking-wider text-slate-400" data-testid="signal-breakdown-total">
            <span>{t("signal.total")}{reasons.reduce((a, r) => a + r.points, 0) > 100 ? ` · ${t("signal.capped")}` : ""}</span>
            <span className="font-bold text-slate-100 tabular-nums">{reasons.reduce((a, r) => a + r.points, 0)}{reasons.reduce((a, r) => a + r.points, 0) > 100 ? ` → ${score}` : ""}</span>
          </li>
        )}
      </ul>
      <p className="mt-4 border-t border-[#1F293D] pt-3 text-[11px] leading-relaxed text-slate-500" data-testid="signal-disclaimer">
        {t("signal.disclaimer")}
      </p>
      <Link to="/hype" data-testid="hype-explainer-link" className="mt-2 inline-block font-mono2 text-[10px] uppercase tracking-wider text-[#00F0FF] hover:underline">
        {t("hype.link")}
      </Link>
      {signal?.computed_at && (
        <p className="mt-2 font-mono2 text-[10px] uppercase tracking-wider text-slate-600">
          {t("signal.computed_at")}: {new Date(signal.computed_at).toLocaleString(lang === "fr" ? "fr-FR" : "en-GB")}
        </p>
      )}
    </div>
  );
}

export function NewsPanel({ news, t, lang }) {
  if (!news || !news.headlines?.length) return null;
  return (
    <div className="mt-6 rounded-xl p-5 hud-card" data-testid="player-news-panel">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-display text-xl font-bold uppercase tracking-tight text-slate-100">{t("player.news")}</h2>
        {news.rumored_play && (
          <span className="rounded-full border border-[#FFD600]/60 bg-[#FFD600]/10 px-2 py-0.5 font-mono2 text-[10px] font-bold uppercase tracking-wider text-[#FFD600]" data-testid="rumored-play-badge">
            {t("player.rumored_badge")}
          </span>
        )}
      </div>
      <div className="space-y-2.5">
        {news.headlines.slice(0, 5).map((h) => (
          <a key={h.link || h.title} href={h.link} target="_blank" rel="noopener noreferrer" className="block rounded-lg border border-[#1F293D] bg-[#0B0E14]/70 px-3 py-2 transition-colors hover:border-[#00F0FF]/50" data-testid="news-headline">
            <p className="text-xs leading-snug text-slate-200">{h.title}</p>
            <p className="mt-1 font-mono2 text-[10px] uppercase tracking-wider text-slate-500">
              {h.source}{h.published ? ` · ${fmtDate(h.published, lang)}` : ""}
            </p>
          </a>
        ))}
      </div>
    </div>
  );
}

export function MatchesPanel({ matches, t, lang }) {
  return (
    <div className="mt-6 rounded-xl p-5 hud-card">
      <h2 className="mb-1 font-display text-xl font-bold uppercase tracking-tight text-slate-100">{t("player.matches")}</h2>
      {/* The two blocks of the page run in opposite directions on purpose (a timeline reads
          forwards, a match list reads backwards), so each one states its direction. */}
      <p className="mb-3 font-mono2 text-[10px] uppercase tracking-wider text-slate-500" data-testid="matches-order-note">{t("player.order_newest")}</p>
      {matches.length === 0 && <p className="text-sm text-slate-500">{t("player.no_matches")}</p>}
      <div className="space-y-2">
        {matches.map((m) => (
          <div key={m.fixture_id} className="flex items-center justify-between gap-2 rounded-lg border border-[#1F293D] bg-[#0B0E14]/70 px-3 py-2 text-xs" data-testid="player-match-row">
            <div className="min-w-0">
              <p className="truncate text-slate-300">{m.match}</p>
              <p className="font-mono2 text-[10px] text-slate-500">{fmtDate(m.date, lang)}</p>
            </div>
            <div className="shrink-0 text-right font-mono2 text-[10px] uppercase tracking-wider text-slate-400">
              <span className={m.started ? "text-[#FFB800]" : m.played ? "text-[#00F0FF]" : "text-slate-500"}>
                {m.started ? t("player.started") : m.played ? t("player.sub") : t("player.dnp")}
              </span>
              {m.minutes != null && <span className="ml-2">{m.minutes}&prime;</span>}
              {(m.goals > 0 || m.assists > 0) && (
                <span className="ml-2 text-[#00FF87]">{m.goals}{t("player.goals")} {m.assists}{t("player.assists")}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const NUM_RE = /^\s*([A-Za-z-]*)\s*-?\s*(\d+)/;
export function cardSortKey(num) {
  const m = NUM_RE.exec(String(num || ""));
  return m ? [m[1].toUpperCase(), parseInt(m[2], 10), String(num)] : ["~", 0, String(num || "")];
}
const cmpCards = (a, b) => {
  const ka = cardSortKey(a.card_number), kb = cardSortKey(b.card_number);
  return ka[0].localeCompare(kb[0]) || ka[1] - kb[1] || ka[2].localeCompare(kb[2]);
};

function SetBlock({ group, open, onToggle, t, market }) {
  return (
    <div className="border-t border-[#1F293D] first:border-t-0" data-testid={`player-cards-set-${group.key}`}>
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center gap-3 py-2.5 text-left" data-testid={`player-cards-set-toggle-${group.key}`}>
        <span className="font-mono2 text-[10px] text-slate-500">{open ? "▾" : "▸"}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-slate-100">{group.set_name}</span>
          <span className="font-mono2 text-[10px] uppercase tracking-wider text-slate-500">{group.manufacturer} · {group.season}</span>
        </span>
        <span className="font-mono2 text-[10px] text-slate-400" data-testid={`player-cards-set-count-${group.key}`}>{group.cards.length}</span>
      </button>
      {open && (
        <ul className="mb-2 divide-y divide-[#1F293D]/70" data-testid={`player-cards-set-rows-${group.key}`}>
          {group.cards.map((c) => (
            <li key={c.id} className="flex items-center gap-3 py-1.5" data-testid={`player-card-row-${c.id}`}>
              <Link to={`/cards/${c.id}`} data-testid="player-card-link" className="flex min-w-0 flex-1 items-center gap-3 hover:text-[#FFD600]">
                <span className="font-mono2 text-base font-bold text-slate-100 tabular-nums">#{c.card_number || "—"}</span>
                <span className="truncate font-mono2 text-[10px] uppercase tracking-wider text-slate-400">{c.subset || c.parallel || "Base"}</span>
                {c.is_rookie === true && <span className="shrink-0 rounded-full border border-[#FFD600]/50 bg-[#FFD600]/10 px-2 py-0.5 font-mono2 text-[9px] font-bold text-[#FFD600]">RC</span>}
                {c.owned_qty > 0 && <span className="shrink-0 rounded-full bg-[#00F0FF]/15 px-2 py-0.5 font-mono2 text-[9px] font-bold uppercase tracking-wider text-[#00F0FF]" data-testid={`player-card-owned-${c.id}`}>{t("sets.coll_owned_badge")}{c.owned_qty > 1 ? ` ×${c.owned_qty}` : ""}</span>}
              </Link>
              {market && (
                <a href={market.url.replace("{QUERY}", encodeURIComponent(`${c.player_name_raw || ""} ${group.set_name} ${c.card_number || ""}`.trim()))} target="_blank" rel="noopener noreferrer nofollow" className="shrink-0 font-mono2 text-[9px] uppercase tracking-wider text-slate-500 hover:text-[#00F0FF]" data-testid={`player-card-market-${c.id}`}>
                  {t("sets.check_listings")} ↗
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function CardsPanel({ cards, t, market }) {
  const groups = useMemo(() => {
    const map = new Map();
    (cards || []).forEach((c) => {
      const key = c.set_id || `${c.manufacturer}|${c.set_name}|${c.season}`;
      if (!map.has(key)) map.set(key, { key, set_name: c.set_name, manufacturer: c.manufacturer, season: c.season, cards: [] });
      map.get(key).cards.push(c);
    });
    const out = [...map.values()];
    out.forEach((g) => g.cards.sort(cmpCards));
    out.sort((a, b) => String(b.season || "").localeCompare(String(a.season || "")) || String(a.set_name || "").localeCompare(String(b.set_name || "")));
    return out;
  }, [cards]);
  const [openKeys, setOpenKeys] = useState(null);
  const open = openKeys ?? new Set(groups.slice(0, 1).map((g) => g.key));
  const toggle = (k) => setOpenKeys((cur) => { const n = new Set(cur ?? open); n.has(k) ? n.delete(k) : n.add(k); return n; });
  return (
    <div className="rounded-xl p-5 hud-card" data-testid="player-cards-section-wrapper">
      <h2 className="mb-1 font-display text-xl font-bold uppercase tracking-tight text-slate-100">{t("cards.title")} <span className="font-mono2 text-xs text-slate-500">{(cards || []).length}</span></h2>
      {(cards || []).length === 0 && <p className="text-sm text-slate-500">{t("cards.none_public")}</p>}
      {groups.map((g) => <SetBlock key={g.key} group={g} open={open.has(g.key)} onToggle={() => toggle(g.key)} t={t} market={market} />)}
    </div>
  );
}

export function MilestonesPanel({ milestones, t, lang }) {
  // The timeline is read as a PATH, not as a flat list: milestones are grouped by the club
  // the player belonged to, and a club change is the separator between two blocks. A block
  // with nothing in it says so ("no event yet with Barcelona") instead of leaving the reader
  // wondering why the change sits at the bottom.
  const blocks = useMemo(() => {
    const out = [];
    const open = (id, name, logo) => { out.push({ id, name, logo, items: [], change: null }); return out[out.length - 1]; };
    (milestones || []).forEach((m) => {
      if (m.type === "transfer") {
        const [from, to] = (m.detail || "").split(" -> ");
        // Un changement vers le club où l'on se trouve déjà n'est pas un changement : la
        // fiche 518384 affichait « Racing Santander → Racing Santander » puis un bloc vide
        // (23/09/2026). La donnée est purgée côté serveur ; ici on ne l'affiche jamais.
        const cur = out[out.length - 1];
        const sameClub = m.from_club_id === m.to_club_id
          || (from && to && from.trim() === to.trim())
          || (cur && ((cur.id && cur.id === m.to_club_id) || (cur.name && cur.name === to)));
        if (sameClub) return;
        const block = cur || open(m.from_club_id, from, m.from_club_logo);
        block.change = m;
        open(m.to_club_id, to, m.to_club_logo);
        return;
      }
      const cur = out[out.length - 1];
      if (!cur || (m.club_id && cur.id && m.club_id !== cur.id)) open(m.club_id, m.club_name, m.club_logo);
      const block = out[out.length - 1];
      if (!block.name && m.club_name) { block.name = m.club_name; block.logo = m.club_logo; block.id = m.club_id; }
      block.items.push(m);
    });
    return out;
  }, [milestones]);

  return (
    <div className="mt-6 rounded-xl p-5 hud-card">
      <h2 className="mb-1 font-display text-xl font-bold uppercase tracking-tight text-slate-100">{t("player.milestones")}</h2>
      <p className="mb-4 font-mono2 text-[10px] uppercase tracking-wider text-slate-500" data-testid="milestones-order-note">{t("player.order_oldest")}</p>
      {milestones.length === 0 && <p className="text-sm text-slate-500">{t("state.none")}</p>}
      {blocks.map((b, bi) => (
        <div key={`${b.id || "club"}-${bi}`} className="mb-5 last:mb-0" data-testid={`milestone-club-block-${b.id || bi}`}>
          {(b.name || b.logo) && (
            <p className="mb-2 flex items-center gap-2 font-mono2 text-[11px] font-bold uppercase tracking-widest text-slate-300">
              {b.logo && <img src={b.logo} alt="" loading="lazy" className="h-4 w-4 shrink-0 object-contain" data-testid={`milestone-block-logo-${b.id || bi}`} />}
              {b.name}
            </p>
          )}
          {b.items.length === 0 && (
            <p className="font-mono2 text-[10px] uppercase tracking-wider text-slate-500" data-testid={`milestone-block-empty-${b.id || bi}`}>
              {t("milestone.no_event_yet", { club: b.name || "" })}
            </p>
          )}
          <ol className="relative space-y-5 border-l border-[#1F293D] pl-5">
            {b.items.map((m) => {
              const Icon = MILESTONE_ICONS[m.type] || Star;
              return (
                <li key={`${m.type}-${m.date}-${m.match_ref || m.detail || ""}`} className="relative" data-testid="milestone-timeline-item">
                  <span className="absolute -left-[27px] flex h-5 w-5 items-center justify-center rounded-full border border-[#00FF87]/60 bg-[#0B0E14] shadow-[0_0_10px_rgba(0,255,135,0.35)]">
                    <Icon className="h-2.5 w-2.5 text-[#00FF87]" />
                  </span>
                  <p className="font-display text-lg font-semibold uppercase tracking-tight text-slate-100">
                    {milestoneLabel(m, t)}
                    <span className={`ml-2 rounded-full border px-1.5 py-0.5 font-mono2 text-[9px] uppercase tracking-wider ${m.confidence === "confirmed" ? "border-[#00FF87]/50 text-[#00FF87]" : m.confidence === "pending" ? "border-[#1F293D] text-slate-500" : "border-[#FFD600]/50 text-[#FFD600]"}`} data-testid={`milestone-confidence-${m.type}`}>
                      {t(`milestone.${m.confidence || "unconfirmed"}`)}
                    </span>
                  </p>
                  {/* Une ligne, un fait (règle du 22/09/2026, comme les motifs du score) :
                      le match et sa date quand elle est prouvée, sinon « le {date} ou
                      avant ». Les précautions sur ce que nous ne voyons pas vivent dans la
                      page Hype, bloc « Ce que nous ne voyons pas ». */}
                  {m.date ? (
                    <p className="mt-0.5 font-mono2 text-[10px] uppercase tracking-wider text-slate-500" data-testid={`milestone-date-${m.type}`}>
                      {[m.detail, fmtDate(m.date, lang)].filter(Boolean).join(" · ")}
                    </p>
                  ) : m.earliest_processed_date ? (
                    <p className="mt-0.5 font-mono2 text-[10px] uppercase tracking-wider text-slate-500" data-testid={`milestone-date-${m.type}`}>
                      {t("milestone.or_earlier_on", { date: fmtDate(m.earliest_processed_date, lang) })}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ol>
          {b.change && (
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-dashed border-[#1F293D] pt-3" data-testid="milestone-timeline-item">
              <ArrowLeftRight className="h-3.5 w-3.5 shrink-0 text-[#00F0FF]" />
              <span className="font-mono2 text-[10px] font-bold uppercase tracking-widest text-[#00F0FF]">
                {t(`milestone.${TRANSFER_KIND[String(b.change.transfer_type || "").toLowerCase()] || "transfer"}`)}
              </span>
              {b.change.from_club_logo && <img src={b.change.from_club_logo} alt="" loading="lazy" className="h-3.5 w-3.5 shrink-0 object-contain" data-testid="milestone-club-logo-from" />}
              <span className="text-slate-600">→</span>
              {b.change.to_club_logo && <img src={b.change.to_club_logo} alt="" loading="lazy" className="h-3.5 w-3.5 shrink-0 object-contain" data-testid="milestone-club-logo-to" />}
              {/* A club change has NO provable date: only the observation day is certain, and
                  it is labelled as such, framed by the matches we already hold. */}
              <span className="font-mono2 text-[10px] uppercase tracking-wider text-slate-500" data-testid="milestone-date-transfer">
                {b.change.date
                  ? t("milestone.change_on", { date: fmtDate(b.change.date, lang) })
                  : b.change.window_start
                  ? t("milestone.change_between", { from: fmtDate(b.change.window_start, lang), to: fmtDate(b.change.window_end || b.change.detected_date, lang) })
                  : t("milestone.change_observed", { date: fmtDate(b.change.detected_date || b.change.date, lang) })}
                {/* A proven date comes from the provider's own transfer record; without it,
                    only the observation day is certain and it is labelled as such. */}
                {!b.change.date && <span className="ml-1 text-slate-600">· {t("milestone.date_unproven")}</span>}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
